import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import * as _ from "lodash";
import { of } from "rxjs";
import { catchError, map, switchMap, withLatestFrom } from "rxjs/operators";

import { MpAdminService } from "../../../services/api/mp-admin.service";
import { MpAdminActions } from "../../actions/mp-admin.actions";
import { FailState } from "../../models/fail-state";
import { ConsumersState } from "../../models/mp-admin/consumers-state";
import { AppState } from "../../reducers";
import { MpAdminSelectors } from "../../selectors/mp-admin.selectors";

@Injectable()
export class ConsumersGetEffects {
	get = createEffect(() =>
		this.actions.pipe(
			ofType(MpAdminActions.consumers.get),
			withLatestFrom(this.store.select(MpAdminSelectors.consumers)),
			switchMap(([action, state]) =>
				this.canUseStoreValue(action, state)
					? of(MpAdminActions.consumers.getSuccess(state))
					: of(MpAdminActions.consumers.getApi({ params: action.params }))
			)
		)
	);

	getApi = createEffect(() =>
		this.actions.pipe(
			ofType(MpAdminActions.consumers.getApi),
			withLatestFrom(this.store.select(MpAdminSelectors.consumers)),
			switchMap(([action, state]) =>
				this.mpAdminService.consumers(action.params).pipe(
					map((consumers) => {
						const allRetrieved = state.allRetrieved || this.noParams(action);
						return MpAdminActions.consumers.getSuccess({
							consumers,
							loading: false,
							allRetrieved
						});
					}),
					catchError((error) =>
						of(MpAdminActions.consumers.getFail(new FailState(error)))
					)
				)
			)
		)
	);

	constructor(
		private mpAdminService: MpAdminService,
		private actions: Actions,
		private store: Store<AppState>
	) {}

	private canUseStoreValue(action, state: ConsumersState): boolean {
		const allQuery = this.noParams(action);
		const ids = _.split(_.get(action, "params.id", "noop"), ",");
		const storeIds = _.map(state.consumers, (c) => c.id);
		return !_.has(state, "error") && !state.loading && allQuery
			? state.allRetrieved
			: _.every(ids, (id) => _.includes(storeIds, id));
	}

	private noParams(action): boolean {
		return !action.params || _.keys(action.params).length === 0;
	}
}
